import ActionButton from 'components/buttons/action-button/ActionButton';
import ConfirmModal from 'components/modal/confirm-modal/ConfirmModal';
import { withModal } from 'components/modal/ModalHOC';
import PromptModal from 'components/modal/prompt-modal/PromptModal';
import Tooltip from 'components/Tooltip';
import { declineEntertainmentVideo, publishEntertainmentVideo } from 'modules/entertainment-videos/reducers';
import {
  EntertainmentVideoStatus,
  FullEntertainmentVideo
} from 'modules/entertainment-videos/types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isDecliningSelector, isPublishingSelector } from 'selectors/entertainment-video';
import { __t, applyTranslations } from 'utils/language';
import { EntertainmentVideoAudio } from '../entertainment-video-audio';
import { EntertainmentVideoProductReviewToggle } from '../entertainment-video-product-review-toggle';
import { EntertainmentVideoCountries } from '../entertainment-video-countries';
import { EntertainmentVideoDeclineHistory } from '../entertainment-video-decline-history';
import { EntertainmentVideoGenders } from '../entertainment-video-genders';
import { EntertainmentVideoItems } from '../entertainment-video-items';
import { EntertainmentVideoMedia } from '../entertainment-video-media';
import { EntertainmentVideoSection } from '../entertainment-video-section';
import { EntertainmentVideoTags } from '../entertainment-video-tags';
import { EntertainmentVideoTimeframe } from '../entertainment-video-timeframe';
import { getInvalidFields } from './helpers';
import './EntertainmentVideoReviewDetails.scss';

applyTranslations(require('./entertainment-video-review-details.i18n.json'));

function translate(path: string) {
  return __t(`entertainmentVideoReviewDetails${path}`);
}

export function EntertainmentVideoReviewDetails({
  pageType,
  offerId,
  deliverableId,
  fullEntertainmentVideo
}: {
  pageType: 'offer' | 'content',
  offerId: number,
  deliverableId: number,
  fullEntertainmentVideo: FullEntertainmentVideo
}) {
  const {
    entertainmentVideo: {
      genders,
      tags,
      publishedCountries,
      publishedFrom,
      publishedUntil,
      declineHistories,
      status,
      audioType,
      isProductReview
    },
    items
  } = fullEntertainmentVideo;

  const isPending = status === EntertainmentVideoStatus.PENDING;
  const isDeclined = status === EntertainmentVideoStatus.DECLINED;
  const isPublishFailed = status === EntertainmentVideoStatus.PUBLISH_FAILED;
  const isUnpublished = status === EntertainmentVideoStatus.UNPUBLISHED;

  return (
    <div>
      {fullEntertainmentVideo.media && (
        <EntertainmentVideoSection>
          <EntertainmentVideoMedia
            key={fullEntertainmentVideo.entertainmentVideo.id}
            thumbnailUrl={fullEntertainmentVideo.media.thumbnailUrl}
            videoUrl={fullEntertainmentVideo.media.url}
          />
        </EntertainmentVideoSection>
      )}
      <EntertainmentVideoSection>
        <EntertainmentVideoProductReviewToggle value={isProductReview} />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.audio')}>
        <EntertainmentVideoAudio audioType={audioType ?? null} />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.gender')}>
        <EntertainmentVideoGenders genders={genders} />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.tags')}>
        <EntertainmentVideoTags tags={tags ?? []} />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.countries')}>
        <EntertainmentVideoCountries countries={publishedCountries} />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.timeframe')}>
        <EntertainmentVideoTimeframe
          publishedFrom={publishedFrom}
          publishedUntil={publishedUntil}
        />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.items')}>
        <EntertainmentVideoItems items={items} />
      </EntertainmentVideoSection>
      {(isPending || isDeclined || isPublishFailed || isUnpublished) && (
        <React.Fragment>
          <hr style={{ margin: '20px 0px' }} />
          <EntertainmentVideoSection narrow>
            {isDeclined && (
              <EntertainmentVideoSection
                title={translate('.sections.declinedReason')}
              >
                <EntertainmentVideoDeclineHistory
                  declineHistory={declineHistories}
                />
              </EntertainmentVideoSection>
            )}
            {(isPending || isPublishFailed || isUnpublished) && (
              <EntertainmentVideoSection>
                {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
                <Actions
                  pageType={pageType}
                  offerId={offerId}
                  deliverableId={deliverableId}
                  fullEntertainmentVideo={fullEntertainmentVideo}
                />
              </EntertainmentVideoSection>
            )}
          </EntertainmentVideoSection>
        </React.Fragment>
      )}
    </div>
  );
}

const DeclineButton = withModal(
  <ActionButton>{translate('.labels.decline')}</ActionButton>,
  {
    modalContent: PromptModal
  }
);

function Actions({
  pageType,
  offerId,
  deliverableId,
  fullEntertainmentVideo
}: {
  pageType: 'offer' | 'content',
  offerId: number,
  deliverableId: number,
  fullEntertainmentVideo: FullEntertainmentVideo
}) {
  const dispatch = useDispatch();

  const isDeclining = useSelector(isDecliningSelector);
  const isPublishing = useSelector(isPublishingSelector);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const isPending = fullEntertainmentVideo.entertainmentVideo.status === EntertainmentVideoStatus.PENDING;
  const publishFailed = fullEntertainmentVideo.entertainmentVideo.status === EntertainmentVideoStatus.PUBLISH_FAILED;

  function handleDecline(declinedReason: string): void {
    if (!isDeclining) {
      dispatch(
        declineEntertainmentVideo({
          pageType,
          offerId,
          deliverableId,
          entertainmentVideoId: fullEntertainmentVideo.entertainmentVideo.id,
          declinedReason
        })
      );
    }
  }

  function publish(): void {
    if (!isPublishing) {
      dispatch(publishEntertainmentVideo({
        pageType,
        offerId,
        deliverableId,
        entertainmentVideoId: fullEntertainmentVideo.entertainmentVideo.id
      }));
    }
  }

  function closeConfirmationModal(): void {
    setIsConfirmModalOpen(false);
  }

  const invalidFields = getInvalidFields(fullEntertainmentVideo);
  const canPublish = invalidFields.length === 0;

  function getValidationErrorMessages(): Array<string> {
    return invalidFields.map(field => translate(`.validationErrors.${field}`));
  }

  let publishButton = (
    <ActionButton
      className="m-0"
      color="primary"
      disabled={isPublishing || !canPublish}
      disabledWithSpinner={isPublishing}
      onClick={() => {
        setIsConfirmModalOpen(true);
      }}
    >
      {translate('.labels.publish')}
    </ActionButton>
  );

  if (!canPublish) {
    publishButton = (
      <Tooltip
        component={
          <React.Fragment>
            {getValidationErrorMessages().map(errorMessage => (
              <div key={errorMessage}>{errorMessage}</div>
            ))}
          </React.Fragment>
        }
        width="auto-compact"
      >
        {publishButton}
      </Tooltip>
    );
  }

  return (
    <React.Fragment>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
        {(isPending || publishFailed) && (
          <DeclineButton
            disabled={isDeclining}
            disabledWithSpinner={isDeclining}
            modalContentProps={{
              onSubmit: handleDecline,
              title: translate('.declineModal.title'),
              label: translate('.declineModal.inputLabel'),
              value: '',
              multiline: true,
              okBtnText: translate('.declineModal.buttonLabel')
            }}
          />
        )}
        {publishButton}
      </div>
      <ConfirmModal
        open={isConfirmModalOpen}
        title={translate('.publishModal.title')}
        confirmButtonColor="primary"
        confirmButtonLabel={translate('.publishModal.buttonLabel')}
        onConfirm={() => {
          publish();
          closeConfirmationModal();
        }}
        onCancel={() => {
          closeConfirmationModal();
        }}
        onClose={() => {
          closeConfirmationModal();
        }}
      />
    </React.Fragment>
  );
}
